import {
  nullableCreatedByUserUuid,
  timestamps,
  uuid,
} from '@snap-types/models/common';
import { PriceStrategy, priceStrategySchema } from '@snap-types/models/enums';
import { schema } from '@snap-types/schemas';
import { z } from 'zod/v4';

const d = schema.t.dec;

export const columns = {
  uuid,
  sortOrder: z.int().default(0),
  priceStrategyType: priceStrategySchema,
  snapSiteConfigUuid: uuid,
  enabled: z.boolean().default(true),
  allowFallback: z.boolean().default(false),

  // Optional fields based on price strategy type
  discountCode: z.string().nullish(),
  marketCode: z.string().nullish(),
  targetContributionMargin: d.fromUnknown.nullish(),
  quantityRange: d.fromUnknown.nullish(),
  targetGrossMargin: d.fromUnknown.nullish(),

  ...nullableCreatedByUserUuid,
  ...timestamps,

  deletedAt: z.date().nullish(),
} as const;

// FixedCustomer, FixedDefault - no additional fields
export const fixedCustomerColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.FixedCustomer),
});

export const fixedDefaultColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.FixedDefault),
});

// FixedDiscount
export const fixedDiscountColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.FixedDiscount),
  discountCode: z.string(),
});

// FixedMarket, RAFFixedMarket
export const fixedMarketColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.FixedMarket),
  marketCode: z.string().min(1, { message: 'Market Code is required' }),
});

export const rafFixedMarketColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.RAFFixedMarket),
  marketCode: z.string().min(1, { message: 'Market Code is required' }),
});

// UserImport, UserPrice, NewPartUserPrice - no additional fields
export const userImportColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.UserImport),
});

export const userPriceColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.UserPrice),
});

export const serviceChargeColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.ServiceCharge),
});

export const newPartUserPriceColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.NewPartUserPrice),
});

export const alphaColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.Alpha),
});

export const newPartUserCostColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.NewPartUserCost),
  targetGrossMargin: d.fromUnknown
    .check(d.checks.gte(0))
    .check(d.checks.lte(1)),
});

// TargetGrossMargin, RAFTargetGrossMargin, NewPart
export const targetGrossMarginColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.TargetGrossMargin),
  targetGrossMargin: d.fromUnknown
    .check(d.checks.gte(0))
    .check(d.checks.lte(1)),
});

export const rafTargetGrossMarginColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.RAFTargetGrossMargin),
  targetGrossMargin: d.fromUnknown
    .check(d.checks.gte(0))
    .check(d.checks.lte(1)),
});

export const newPartColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.NewPart),
  targetGrossMargin: d.fromUnknown
    .check(d.checks.gte(0))
    .check(d.checks.lte(1)),
});

// TargetContributionMargin
export const targetContributionMarginColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.TargetContributionMargin),
  targetContributionMargin: d.fromUnknown
    .check(d.checks.gte(0))
    .check(d.checks.lte(1)),
});

// PriceSegmentation, PriceSegmentationWithTargetGrossMargin, CAPPriceSegmentation
export const priceSegmentationColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.PriceSegmentation),
  quantityRange: d.fromUnknown,
});

export const priceSegmentationWithTargetGrossMarginColumns = z.object({
  priceStrategyType: z.literal(
    PriceStrategy.PriceSegmentationWithTargetGrossMargin,
  ),
  targetGrossMargin: d.fromUnknown
    .check(d.checks.gte(0))
    .check(d.checks.lte(1)),
});

export const capPriceSegmentationColumns = z.object({
  priceStrategyType: z.literal(PriceStrategy.CAPPriceSegmentation),
  targetGrossMargin: d.fromUnknown
    .check(d.checks.gte(0))
    .check(d.checks.lte(1)),
});
